import { useEffect, useState } from 'react';
import { getLocalStorage } from '@shared/core';

/**
 * Which WebsiteV2 onboarding the createwedding funnel offers this visitor:
 *
 * - 'off'       — no choice screen; today's classic flow untouched (pilot control).
 * - 'templates' — beta pitched as all-new designer templates + the new editor; the AI
 *                 brief path is hidden entirely.
 * - 'ai'        — the full experience: templates AND the AI "design it around your story" path.
 *
 * Both beta variants land in the same WYSIWYG editor — the pilot isolates whether an
 * AI-generated starting point beats a template starting point given the same editor.
 *
 * SPIKE / DEMO: the variant persists in localStorage and **⌃⇧B (Ctrl+Shift+B — "builder")
 * toggles ai ⇄ templates** live (any funnel page with this hook mounted, outside text fields;
 * logged to the console). Plain Ctrl deliberately — ⌘-combos collide with Raycast/system
 * bindings on mac. Default 'ai'. For the
 * pilot this store becomes an Amplitude experiment read (e.g. `useFeatureValue('websiteV2Onboarding')`)
 * whose variant maps onto this union — assignment, exposure logging, and dashboards come free.
 * Keep the return shape so consumers don't change.
 */
export type WebsiteV2OnboardingVariant = 'off' | 'templates' | 'ai';

const STORAGE_KEY = '@@joy/websiteV2OnboardingVariant';
const DEFAULT_VARIANT: WebsiteV2OnboardingVariant = 'ai';

const isVariant = (value: unknown): value is WebsiteV2OnboardingVariant => value === 'off' || value === 'templates' || value === 'ai';

const readStoredVariant = (): WebsiteV2OnboardingVariant => {
  const stored = getLocalStorage().getItem(STORAGE_KEY);
  return isVariant(stored) ? stored : DEFAULT_VARIANT;
};

// Tiny module store so every mounted consumer (form controller, tying-the-knot) sees one value
// and the keydown listener registers exactly once regardless of how many are mounted.
const subscribers = new Set<(variant: WebsiteV2OnboardingVariant) => void>();
let isKeydownRegistered = false;

const setVariantEverywhere = (variant: WebsiteV2OnboardingVariant): void => {
  getLocalStorage().setItem(STORAGE_KEY, variant);
  subscribers.forEach(notify => notify(variant));
};

const ensureKeydownListener = (): void => {
  if (isKeydownRegistered || typeof window === 'undefined') return;
  isKeydownRegistered = true;
  window.addEventListener(
    'keydown',
    event => {
      if (!(event.ctrlKey && !event.metaKey && event.shiftKey && event.code === 'KeyB')) return;
      // Leave text fields alone.
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      event.preventDefault();
      const next: WebsiteV2OnboardingVariant = readStoredVariant() === 'ai' ? 'templates' : 'ai';
      setVariantEverywhere(next);
      // eslint-disable-next-line no-console
      console.info(`[WebsiteV2] onboarding variant → ${next}`);
    },
    // Capture phase: page components stop propagation of some keydowns before they'd bubble
    // back to window — observed live 2026-09-03 (synthetic dispatch worked, real presses didn't).
    true,
  );
};

export const useWebsiteV2OnboardingVariant = (): Readonly<{ variant: WebsiteV2OnboardingVariant; isLoading: boolean }> => {
  const [variant, setVariant] = useState<WebsiteV2OnboardingVariant>(readStoredVariant);

  useEffect(() => {
    ensureKeydownListener();
    subscribers.add(setVariant);
    // Re-sync in case another consumer toggled between this component's render and this effect.
    setVariant(readStoredVariant());
    return () => {
      subscribers.delete(setVariant);
    };
  }, []);

  return { variant, isLoading: false };
};
